import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '@/store/app';
import { useAuthStore } from '@/store/auth';
import { api, type Reporte } from '@/lib/api';
import ExportarPDF from './ExportarPDF';

const ESTADO_COLOR: Record<string, string> = { pendiente: '#ca8a04', proceso: '#2563eb', resuelto: '#22c55e' };
const ESTADO_LABEL: Record<string, string> = { pendiente: 'Pendiente', proceso: 'En proceso', resuelto: 'Resuelto' };
const PRIORIDAD_COLOR: Record<string, string> = { alta: '#ef4444', media: '#ca8a04', baja: '#22c55e' };
const PRIORIDAD_LABEL: Record<string, string> = { alta: 'Alta', media: 'Media', baja: 'Baja' };

function KpiCard({ label, value, color, sub }: { label: string; value: number; color: string; sub?: string }) {
  return (
    <div style={{ flex: 1, minWidth: 90, background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(10px)', borderRadius: 14, padding: '12px 14px', border: `1.5px solid ${color}28` }}>
      <div style={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: 'rgba(10,22,40,0.55)', marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 9.5, color: 'rgba(10,22,40,0.35)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function Seguimiento() {
  const { filtroEstado, filtroSector, query, sortBy, selectedTicketId, setFiltroEstado, setFiltroSector, setQuery, toggleSort, selectTicket } = useAppStore();
  const { token, institucion } = useAuthStore();
  const qc = useQueryClient();

  const params: Record<string, string> = {};
  if (institucion?.id !== 'todos' && institucion?.id) params.institucionId = institucion.id;
  if (filtroEstado) params.estado = filtroEstado;
  if (filtroSector) params.sector = filtroSector;
  if (query) params.q = query;
  if (sortBy) params.sortBy = sortBy;

  const { data: reportes = [], isLoading } = useQuery<Reporte[]>({
    queryKey: ['reportes', params],
    queryFn: () => api.reportes(token!, Object.keys(params).length ? params : undefined),
    enabled: !!token,
  });

  const ciclar = useMutation({
    mutationFn: (id: string) => api.ciclarReporte(token!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reportes'] }),
  });

  const selected = reportes.find(r => r.id === selectedTicketId);
  const [mostrarPDF, setMostrarPDF] = useState(false);

  const total = reportes.length;
  const pendientes = reportes.filter(r => r.estado === 'pendiente').length;
  const enproceso = reportes.filter(r => r.estado === 'proceso').length;
  const resueltos = reportes.filter(r => r.estado === 'resuelto').length;
  const altas = reportes.filter(r => r.prioridad === 'alta').length;

  const pctResuelto = total > 0 ? Math.round((resueltos / total) * 100) : 0;

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', paddingTop: 80, paddingBottom: 80 }}>

      {/* KPI row */}
      <div style={{ padding: '12px 16px', borderBottom: '0.5px solid rgba(10,22,40,0.07)', background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(12px)' }}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <KpiCard label="Total reportes" value={total} color="#1d4ed8" />
          <KpiCard label="Pendientes" value={pendientes} color="#ca8a04" sub="Sin atender" />
          <KpiCard label="En proceso" value={enproceso} color="#2563eb" sub="En curso" />
          <KpiCard label="Resueltos" value={resueltos} color="#16a34a" sub={`${pctResuelto}% del total`} />
          <KpiCard label="Prioridad alta" value={altas} color="#ef4444" sub="Urgentes" />
        </div>

        {/* Progress bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, height: 6, background: 'rgba(10,22,40,0.08)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: 'linear-gradient(90deg,#1d4ed8,#22c55e)', width: `${pctResuelto}%`, borderRadius: 3, transition: 'width 0.6s ease' }} />
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#16a34a', whiteSpace: 'nowrap' }}>{pctResuelto}% resuelto</span>
        </div>
      </div>

      {/* Filters */}
      <div style={{ padding: '10px 16px', borderBottom: '0.5px solid rgba(10,22,40,0.07)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', background: 'rgba(255,255,255,0.45)', backdropFilter: 'blur(10px)' }}>
        <div style={{ flex: '1 1 150px', display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(10,22,40,0.06)', borderRadius: 10, padding: '6px 10px' }}>
          <i className="ti ti-search" style={{ fontSize: 12, color: 'rgba(10,22,40,0.4)' }} />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Buscar reporte…" style={{ border: 'none', background: 'transparent', fontSize: 12, color: '#0A1628', outline: 'none', width: '100%' }} />
        </div>
        {(['pendiente', 'proceso', 'resuelto'] as const).map(e => (
          <button key={e} onClick={() => setFiltroEstado(filtroEstado === e ? null : e)}
            style={{ padding: '5px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, border: filtroEstado === e ? `1.5px solid ${ESTADO_COLOR[e]}` : '1.5px solid transparent',
              background: filtroEstado === e ? ESTADO_COLOR[e] + '18' : 'rgba(10,22,40,0.06)',
              color: filtroEstado === e ? ESTADO_COLOR[e] : 'rgba(10,22,40,0.5)' }}>
            {ESTADO_LABEL[e]}
          </button>
        ))}
        <button onClick={toggleSort} style={{ padding: '5px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: 'rgba(10,22,40,0.06)', color: 'rgba(10,22,40,0.5)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <i className="ti ti-arrows-sort" style={{ fontSize: 11 }} />
          {sortBy === 'fecha' ? 'Fecha' : 'Prioridad'}
        </button>
        <button onClick={() => setMostrarPDF(true)} disabled={total === 0}
          style={{ padding: '5px 11px', borderRadius: 20, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4,
            background: total > 0 ? '#1D4ED8' : 'rgba(10,22,40,0.06)',
            color: total > 0 ? '#fff' : 'rgba(10,22,40,0.3)' }}>
          <i className="ti ti-file-type-pdf" style={{ fontSize: 11 }} /> Exportar PDF
        </button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Report list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {isLoading && (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <div style={{ width: 30, height: 30, borderRadius: '50%', border: '2.5px solid rgba(29,78,216,0.2)', borderTopColor: '#1D4ED8', animation: 'spin 700ms linear infinite', margin: '0 auto 10px' }} />
              <div style={{ fontSize: 12, color: 'rgba(10,22,40,0.4)' }}>Cargando reportes…</div>
            </div>
          )}
          {!isLoading && reportes.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <i className="ti ti-clipboard-off" style={{ fontSize: 36, color: 'rgba(10,22,40,0.15)', display: 'block', marginBottom: 10 }} />
              <div style={{ fontSize: 12, color: 'rgba(10,22,40,0.35)' }}>Sin reportes para los filtros actuales</div>
            </div>
          )}
          {reportes.map(r => (
            <div key={r.id} onClick={() => selectTicket(r.id)}
              style={{ padding: '11px 16px', borderBottom: '0.5px solid rgba(10,22,40,0.06)', cursor: 'pointer', background: selectedTicketId === r.id ? 'rgba(29,78,216,0.06)' : 'transparent', display: 'flex', gap: 10, alignItems: 'flex-start', transition: 'background 0.15s' }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: r.categoria.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                <i className={`ti ${r.categoria.icon}`} style={{ color: r.categoria.color, fontSize: 15 }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0A1628', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.titulo}</div>
                <div style={{ fontSize: 10, color: 'rgba(10,22,40,0.45)', marginTop: 2 }}>{r.sector} · {r.brigadista}</div>
                <div style={{ display: 'flex', gap: 5, marginTop: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: 9.5, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: ESTADO_COLOR[r.estado] + '18', color: ESTADO_COLOR[r.estado], border: `1px solid ${ESTADO_COLOR[r.estado]}44` }}>{ESTADO_LABEL[r.estado]}</span>
                  <span style={{ fontSize: 9.5, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: PRIORIDAD_COLOR[r.prioridad] + '18', color: PRIORIDAD_COLOR[r.prioridad] }}>{PRIORIDAD_LABEL[r.prioridad]}</span>
                  <span style={{ fontSize: 9.5, color: 'rgba(10,22,40,0.35)' }}>{r.categoria.label}</span>
                </div>
              </div>
              <div style={{ fontSize: 9, color: 'rgba(10,22,40,0.3)', flexShrink: 0, marginTop: 2 }}>
                {new Date(r.createdAt).toLocaleDateString('es-VE', { day: '2-digit', month: 'short' })}
              </div>
            </div>
          ))}
        </div>

        {/* Detail panel */}
        {selected ? (
          <div style={{ width: 290, borderLeft: '0.5px solid rgba(10,22,40,0.1)', overflowY: 'auto', background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(14px)', padding: '20px 18px' }}>
            {/* Category icon */}
            <div style={{ width: 46, height: 46, borderRadius: 14, background: selected.categoria.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginBottom: 12 }}>
              <i className={`ti ${selected.categoria.icon}`} style={{ color: selected.categoria.color }} />
            </div>

            <h3 style={{ fontSize: 15, fontWeight: 800, color: '#0A1628', margin: '0 0 4px' }}>{selected.titulo}</h3>
            <div style={{ fontSize: 11, color: 'rgba(10,22,40,0.4)', marginBottom: 14 }}>{selected.categoria.label} · {selected.sector}</div>

            {selected.descripcion && (
              <div style={{ fontSize: 12, color: 'rgba(10,22,40,0.65)', lineHeight: 1.65, background: 'rgba(10,22,40,0.04)', borderRadius: 10, padding: '10px 12px', marginBottom: 16 }}>
                {selected.descripcion}
              </div>
            )}

            {/* Datos */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 20, border: '0.5px solid rgba(10,22,40,0.08)', borderRadius: 12, overflow: 'hidden' }}>
              {[
                { label: 'Estado', value: ESTADO_LABEL[selected.estado], color: ESTADO_COLOR[selected.estado] },
                { label: 'Prioridad', value: PRIORIDAD_LABEL[selected.prioridad], color: PRIORIDAD_COLOR[selected.prioridad] },
                { label: 'Brigadista', value: selected.brigadista, color: undefined },
                { label: 'Sector', value: selected.sector, color: undefined },
                { label: 'Fecha', value: new Date(selected.createdAt).toLocaleDateString('es-VE', { day: '2-digit', month: 'long', year: 'numeric' }), color: undefined },
              ].map((item, i) => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 12px', background: i % 2 ? 'rgba(10,22,40,0.02)' : 'transparent', borderBottom: i < 4 ? '0.5px solid rgba(10,22,40,0.06)' : 'none' }}>
                  <span style={{ fontSize: 11, color: 'rgba(10,22,40,0.4)' }}>{item.label}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: item.color ?? '#0A1628', maxWidth: 140, textAlign: 'right' }}>{item.value}</span>
                </div>
              ))}
            </div>

            {/* Avanzar estado */}
            {selected.estado !== 'resuelto' && (
              <button onClick={() => ciclar.mutate(selected.id)} disabled={ciclar.isPending}
                style={{ width: '100%', padding: '11px', borderRadius: 12, background: ciclar.isPending ? 'rgba(29,78,216,0.4)' : 'linear-gradient(135deg,#1D4ED8,#2563EB)', color: '#fff', fontWeight: 700, fontSize: 12.5, boxShadow: '0 6px 16px rgba(29,78,216,0.3)', border: 'none', cursor: ciclar.isPending ? 'not-allowed' : 'pointer', transition: 'opacity 0.2s' }}>
                {ciclar.isPending ? 'Actualizando…' : selected.estado === 'pendiente' ? '▶ Iniciar atención' : '✓ Marcar como resuelto'}
              </button>
            )}
            {selected.estado === 'resuelto' && (
              <div style={{ textAlign: 'center', padding: '10px 0', fontSize: 12, color: '#16a34a', fontWeight: 700 }}>
                ✓ Reporte resuelto
              </div>
            )}
          </div>
        ) : (
          <div style={{ width: 290, borderLeft: '0.5px solid rgba(10,22,40,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.3)' }}>
            <div style={{ textAlign: 'center', padding: 24 }}>
              <i className="ti ti-hand-click" style={{ fontSize: 34, color: 'rgba(10,22,40,0.15)', display: 'block', marginBottom: 10 }} />
              <div style={{ fontSize: 12, color: 'rgba(10,22,40,0.35)' }}>Selecciona un reporte</div>
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {mostrarPDF && (
        <ExportarPDF
          reportes={reportes}
          institucion={institucion?.nombre ?? 'ComuniData'}
          filtroEstado={filtroEstado}
          filtroSector={filtroSector}
          onClose={() => setMostrarPDF(false)}
        />
      )}
    </div>
  );
}
